'use client';

import ModalBase from '@/components/ui/ModalBase/ModalBase';
import { getLotteryEventDetail } from '@/features/lottery/api/getLotteryEventDetail';
import { swal } from '@/lib/sweetalert';
import layoutStyles from '@/styles/modules/layout.module.scss';
import { isVersion } from '@/utils/version';
import { SerialCodeModalConfirmUI, SerialCodeModalInputCodeUI, pickErrorMessages } from '@monorepo/common';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { postDrawLotsFromCampaign } from '../../api/postDrawLotsFromCampaign';
import { postSearchCode } from '../../api/postSearchCode';
import { postDrawLotsFromCampaignV2 } from '../../api/v2/postDrawLotsFromCampaign';
import { useIsOpenSerialCodeModal } from '../../state';
import SerialCodeInput from '../SerialCodeInput/SerialCodeInput';

function SerialCodeModal() {
  const searchParams = useSearchParams();
  const [serialCode, setSerialCode] = useState(searchParams.get('serial-code') ?? '');
  const [campaignData, setCampaignData] = useState({
    lotteryEventName: '',
    lotteryEventBranchId: 0,
    lotteryEventBranchName: '',
    caution: '',
    version: null as string | null,
  });
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const [isOpen, setIsOpen] = useIsOpenSerialCodeModal();

  // バージョンに基づいてAPIを選択
  const getDrawLotsFromCampaignAPI = (version: string | null | undefined) => {
    if (isVersion(version, '2.0')) {
      return postDrawLotsFromCampaignV2;
    }
    return postDrawLotsFromCampaign; // v1（versionがnull/undefinedの場合もv1）
  };

  const valid = async () => {
    try {
      const { result } = await postSearchCode(serialCode);

      // 親イベントの詳細を取得してversionを取得
      const { result: eventDetail } = await getLotteryEventDetail({
        id: result.lotteryEventId,
        oemServiceId: Number(process.env.NEXT_PUBLIC_SERVICE_ID),
      });

      setCampaignData({
        lotteryEventName: result.lotteryEventName,
        lotteryEventBranchId: result.lotteryEventBranchId,
        lotteryEventBranchName: result.lotteryEventBranchName,
        caution: result.caution,
        version: eventDetail.version,
      });
      // 確認セクションへ
      setStep(2);

      // もし、くじ詳細ページにいなければくじ詳細に強制遷移
      if (location.pathname !== `/campaign-lotteries/${result.lotteryEventBranchId}`) {
        router.push(`/campaign-lotteries/${result.lotteryEventBranchId}`);
        setIsOpen(false);
      }
    } catch (error) {
      await swal.messages({ messages: pickErrorMessages(error) });
    }
  };

  const draw = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      // バージョンに基づいてAPIを選択して実行
      const selectedAPI = getDrawLotsFromCampaignAPI(campaignData.version);

      const { result } = await selectedAPI({
        lotteryEventBranchId: campaignData.lotteryEventBranchId,
        serialCode: serialCode,
      });
      await goAnimation(result.lotteryDrawHistoryId);

      // 初期化して閉じる
      reset();
      setIsOpen(false);
    } catch (error) {
      await swal.messages({ messages: pickErrorMessages(error) });
    } finally {
      setIsLoading(false);
    }
  };

  // アニメーション
  const goAnimation = (lotteryDrawHistoryId: number) => {
    return router.push(`/draw/${lotteryDrawHistoryId}/anime`);
  };

  const reset = () => {
    setSerialCode('');
    setStep(1);
  };

  const modalContent = () => {
    switch (step) {
      case 1:
        return (
          <SerialCodeModalInputCodeUI
            initialValue={serialCode}
            onChange={setSerialCode}
            valid={valid}
            SerialCodeInputComponent={SerialCodeInput}
          />
        );
      case 2:
        return (
          <SerialCodeModalConfirmUI
            serialCode={serialCode}
            campaignData={campaignData}
            draw={draw}
            isLoading={isLoading}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
    >
      <div className={layoutStyles.modalPadding}>{modalContent()}</div>
    </ModalBase>
  );
}

export default SerialCodeModal;
